/**
 * Animation Utilities & Tailwind Animation Classes
 * Provides smooth transitions and micro-interactions
 */

// Custom animation CSS classes (add to your CSS file or use inline)
export const animationClasses = {
  // Fade animations
  fadeIn: 'animate-fadeIn',
  fadeOut: 'animate-fadeOut',
  fadeInUp: 'animate-fadeInUp',
  fadeInDown: 'animate-fadeInDown',
  
  // Slide animations
  slideInLeft: 'animate-slideInLeft',
  slideInRight: 'animate-slideInRight',
  slideInUp: 'animate-slideInUp',
  slideInDown: 'animate-slideInDown',
  
  // Scale animations
  scaleIn: 'animate-scaleIn',
  scaleOut: 'animate-scaleOut',
  pulse: 'animate-pulse',
  
  // Bounce animations
  bounce: 'animate-bounce',
  bounceIn: 'animate-bounceIn',
  
  // Spin animations
  spin: 'animate-spin',
  spinSlow: 'animate-spin-slow',
  
  // Wiggle/Shake
  wiggle: 'animate-wiggle',
  shake: 'animate-shake',
};

// Tailwind-compatible animation utilities
export const transitions = {
  default: 'transition duration-300 ease-in-out',
  fast: 'transition duration-150 ease-in-out',
  slow: 'transition duration-500 ease-in-out',
  colors: 'transition-colors duration-300 ease-in-out',
  transform: 'transition-transform duration-300 ease-in-out',
  all: 'transition-all duration-300 ease-in-out',
};

// Hover effects
export const hoverEffects = {
  lift: 'hover:transform hover:-translate-y-1 hover:shadow-lg transition-all duration-300',
  scale: 'hover:scale-105 transition-transform duration-300',
  glow: 'hover:shadow-xl hover:shadow-blue-500/50 transition-shadow duration-300',
  brighten: 'hover:brightness-110 transition duration-300',
  underline: 'hover:underline transition duration-300',
};

// Focus effects for accessibility
export const focusEffects = {
  ring: 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
  ringPurple: 'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
  ringGreen: 'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
  ringOrange: 'focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2',
  underline: 'focus:outline-none focus:underline focus:underline-offset-4',
};

// Micro-interactions
export const microInteractions = {
  button: 'active:scale-95 transition-transform duration-150',
  card: 'hover:shadow-lg transition-shadow duration-300',
  input: 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300',
  checkbox: 'focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200',
};

// Stagger animation delays for lists
export const staggerDelays = (index, baseDelay = 50) => {
  return {
    animationDelay: `${index * baseDelay}ms`
  };
};

// Generate keyframe animations for Tailwind
export const keyframes = `
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes scaleOut {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.8);
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes wiggle {
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Animation classes */
.animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
.animate-fadeOut { animation: fadeOut 0.3s ease-in-out; }
.animate-fadeInUp { animation: fadeInUp 0.5s ease-out; }
.animate-fadeInDown { animation: fadeInDown 0.5s ease-out; }
.animate-slideInLeft { animation: slideInLeft 0.4s ease-out; }
.animate-slideInRight { animation: slideInRight 0.4s ease-out; }
.animate-slideInUp { animation: slideInUp 0.4s ease-out; }
.animate-slideInDown { animation: slideInDown 0.4s ease-out; }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
.animate-scaleOut { animation: scaleOut 0.3s ease-out; }
.animate-bounceIn { animation: bounceIn 0.6s ease-out; }
.animate-wiggle { animation: wiggle 0.5s ease-in-out infinite; }
.animate-shake { animation: shake 0.5s ease-in-out; }
.animate-spin-slow { animation: spin-slow 3s linear infinite; }
`;

// Predefined animation combinations
export const animationPresets = {
  modalEnter: 'animate-scaleIn',
  modalExit: 'animate-scaleOut',
  listItem: 'animate-fadeInUp',
  notification: 'animate-slideInRight',
  tooltip: 'animate-fadeIn',
  drawer: 'animate-slideInLeft',
};

export default {
  animationClasses,
  transitions,
  hoverEffects,
  focusEffects,
  microInteractions,
  staggerDelays,
  keyframes,
  animationPresets,
};
